import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import I18n from 'I18n';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module 'I18n... Remove this comment to see the full error message
import FormattedDuration from 'I18n/components/FormattedDuration';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIButton from 'UIComponents/button/UIButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UITooltipContent from 'UIComponents/tooltip/UITooltipContent';
import { OLAF } from 'HubStyleTokens/colors';
import { SaveStates } from 'editor-components/EPConstants';
import { isLastSaveAfterThreshold, isLastSaveToday, getNextDurationChange, fiveSecondDuration } from 'editor-components/utils/durationUtils';
import { withNonTrellisComponent, withTrellisComponent } from '../hoc/withTrellisComponentHOCs';
import { RemoveIcon, SuccessIcon } from 'foundations-assets';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
let durationChangeCheckTimeout;
function defaultGetMessage({
  showLastSaved,
  saveState,
  lastSave,
  now
}) {
  let message;
  if (!lastSave) message = null;else {
    if (isLastSaveToday({
      lastSave,
      now
    })) {
      message = 'epSaveLabel.savedAtToday';
    } else {
      message = 'epSaveLabel.savedAt';
    }
  }
  switch (saveState) {
    case SaveStates.NO_UNSAVED_CHANGES:
      if (!showLastSaved) {
        message = 'epSaveLabel.noChanges';
      }
      break;
    case SaveStates.UNSAVED_CHANGES:
      message = 'epSaveLabel.hasChanges';
      break;
    case SaveStates.SAVING_CHANGES:
      message = 'epSaveLabel.saving';
      break;
    case SaveStates.AUTO_SAVED_CHANGES:
      {
        if (!showLastSaved) {
          message = 'epSaveLabel.autoSaved';
        }
        break;
      }
    case SaveStates.SAVED_CHANGES:
      {
        if (!showLastSaved) {
          message = 'epSaveLabel.saved';
        }
        break;
      }
    case SaveStates.SAVE_FAILED:
      message = 'epSaveLabel.notSaved';
      break;
    default:
      message = null;
  }
  return message;
}
const EPSaveLabel = ({
  buttonUse,
  getMessage = defaultGetMessage,
  iconColor,
  justifyLabel,
  lastSave,
  onClick,
  onDark = true,
  renderTooltipContent,
  saveState,
  showTimestampAfter,
  tooltipPlacement = 'bottom',
  tooltipUse = 'default',
  disabled,
  statusIconPosition = 'left',
  CustomTooltip,
  isTrellisCompatibleComponent
}) => {
  const [now, setNow] = useState(I18n.moment.portalTz().valueOf());
  const getShowTimestampAfter = useCallback(() => {
    if (showTimestampAfter != null) {
      return showTimestampAfter;
    }
    return fiveSecondDuration();
  }, [showTimestampAfter]);
  const getAndUpdateNow = useCallback(() => {
    const updatedNow = I18n.moment.portalTz().valueOf();
    if (lastSave) {
      const timeout = getNextDurationChange({
        lastSave,
        now: updatedNow,
        showTimestampAfter: getShowTimestampAfter()
      });
      if (timeout != null) {
        clearTimeout(durationChangeCheckTimeout);
        durationChangeCheckTimeout = setTimeout(() => setNow(getAndUpdateNow()), +timeout + 1);
      }
    }
    return updatedNow;
  }, [getShowTimestampAfter, lastSave]);
  useEffect(() => {
    setNow(getAndUpdateNow());
  }, [getAndUpdateNow, lastSave]);
  const getTooltipContent = () => {
    if (renderTooltipContent) {
      return renderTooltipContent(now);
    }
    if (!lastSave) {
      return null;
    }
    if (isTrellisCompatibleComponent) {
      return /*#__PURE__*/_jsx(FormattedDateTime, {
        value: lastSave,
        format: "l LT"
      });
    }
    return /*#__PURE__*/_jsx(UITooltipContent, {
      children: /*#__PURE__*/_jsx(FormattedDateTime, {
        value: lastSave,
        format: "l LT"
      })
    });
  };
  function renderIcon() {
    if (saveState === SaveStates.SAVING_CHANGES) {
      return /*#__PURE__*/_jsx(UILoadingSpinner, {
        size: "extra-small",
        use: "primary-white"
      });
    }
    const iconMargin = statusIconPosition === 'left' ? 'm-right-2' : 'm-left-2';
    if (saveState === SaveStates.SAVE_FAILED) {
      if (isTrellisCompatibleComponent) {
        return /*#__PURE__*/_jsx(IconHolder, {
          icon: RemoveIcon,
          color: iconColor !== null && iconColor !== void 0 ? iconColor : 'icon-core-on-fill-default'
        });
      }
      return /*#__PURE__*/_jsx(UIIcon, {
        className: iconMargin,
        color: iconColor !== null && iconColor !== void 0 ? iconColor : OLAF,
        name: "remove"
      });
    }
    if (!isLastSaveAfterThreshold({
      lastSave,
      threshold: getShowTimestampAfter(),
      now
    }) && (saveState === SaveStates.AUTO_SAVED_CHANGES || saveState === SaveStates.SAVED_CHANGES)) {
      if (isTrellisCompatibleComponent) {
        return /*#__PURE__*/_jsx(IconHolder, {
          icon: SuccessIcon,
          color: iconColor !== null && iconColor !== void 0 ? iconColor : 'icon-core-on-fill-default'
        });
      }
      return /*#__PURE__*/_jsx(UIIcon, {
        className: iconMargin,
        color: iconColor !== null && iconColor !== void 0 ? iconColor : OLAF,
        name: "success"
      });
    }
    return null;
  }
  function renderText() {
    const showLastSaved = isLastSaveAfterThreshold({
      lastSave,
      threshold: getShowTimestampAfter(),
      now
    });
    const message = getMessage({
      showLastSaved,
      saveState,
      lastSave,
      now
    });
    return message ? /*#__PURE__*/_jsx(FormattedReactMessage, {
      message: message,
      options: {
        lastSaveDate: lastSave ? /*#__PURE__*/_jsx(FormattedDateTime, {
          value: lastSave,
          format: "LL"
        }) : '',
        lastSaveDuration: lastSave ? /*#__PURE__*/_jsx(FormattedDuration, {
          to: lastSave,
          from: now,
          includeAffix: true
        }) : ''
      }
      // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
      ,
      style: {
        whiteSpace: 'nowrap'
      }
    }) : null;
  }
  let button = /*#__PURE__*/_jsx(UIButton, {
    "data-shepherd-id": "save-revisions",
    "data-test-id": "save-button",
    onClick: onClick,
    use: buttonUse || (onDark ? 'link-on-dark' : 'link-on-bright'),
    disabled: disabled,
    children: renderText()
  });
  if (CustomTooltip) {
    button = /*#__PURE__*/_jsx(CustomTooltip, {
      children: button
    });
  }
  if (renderTooltipContent || lastSave) {
    if (isTrellisCompatibleComponent) {
      button = /*#__PURE__*/_jsx(Tooltip, {
        Content: getTooltipContent,
        placement: tooltipPlacement,
        use: tooltipUse,
        children: button
      });
    } else {
      button = /*#__PURE__*/_jsx(UITooltip, {
        Content: getTooltipContent,
        placement: tooltipPlacement,
        use: tooltipUse,
        children: button
      });
    }
  }
  if (isTrellisCompatibleComponent) {
    return /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      "data-test-id": "autosave-label",
      justifyContent: justifyLabel || 'start',
      flexGrow: 1,
      children: [statusIconPosition === 'left' && renderIcon(), button, statusIconPosition === 'right' && renderIcon()]
    });
  }
  return /*#__PURE__*/_jsxs(UIFlex, {
    align: "center",
    "data-test-id": "autosave-label",
    justify: justifyLabel || 'start',
    children: [statusIconPosition === 'left' && renderIcon(), button, statusIconPosition === 'right' && renderIcon()]
  });
};
EPSaveLabel.propTypes = {
  // @ts-expect-error propTypes and TS are not playing nicely
  buttonUse: PropTypes.string,
  statusIconPosition: PropTypes.oneOf(['left', 'right']),
  iconColor: PropTypes.string,
  lastSave: PropTypes.oneOfType([PropTypes.number, PropTypes.instanceOf(Date)]),
  onDark: PropTypes.bool,
  renderTooltipContent: PropTypes.func,
  // @ts-expect-error propTypes and TS are not playing nicely
  saveState: PropTypes.oneOf(Object.keys(SaveStates)).isRequired,
  showTimestampAfter: PropTypes.number,
  // @ts-expect-error propTypes and TS are not playing nicely
  tooltipPlacement: PropTypes.string,
  // @ts-expect-error propTypes and TS are not playing nicely
  tooltipUse: PropTypes.string
};
export const LegacyEPSaveLabel =
// @ts-expect-error propTypes and TS are not playing nicely
withNonTrellisComponent()(EPSaveLabel);
export const TrellisEPSaveLabel =
// @ts-expect-error propTypes and TS are not playing nicely
withTrellisComponent()(EPSaveLabel);
export default LegacyEPSaveLabel;